import { storableError } from '../../util/errors';
import { unitDivisor } from '../../util/currency';
import {
  parseDateFromISO8601,
  getExclusiveEndDate,
  addTime,
  subtractTime,
  getStartOf,
  addTimewith30mins,
} from '../../util/dates';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { splitBoundsIntoQuadrants } from '../../util/maps';
import {
  isSuperhostActive,
  reorderListingsForSuperhostsAcrossPages,
  makeRawSuperhostPredicate,
} from '../../util/superhost';
import { isOriginInUse, parseSelectFilterOptions } from '../../util/search';
import { resolvePriceRangeRowForGuestRange } from '../../util/capacityFieldUtils';
import {
  GARDEN_FACE,
  POOL_FACE,
  applyFaceToListing,
  dateYmdFromDatesParam,
  faceView,
  isPoolSeasonFor,
  offersPoolUseExtra,
  resolveListingFace,
} from '../../util/listingFaces';
import {
  isInterioresListing,
  isInterioresSearchTab,
  isRainProofListing,
  hasIndoorSpaceListing,
  COVERED_AREA_EXTRA,
  INDOOR_SPACE_EXTRAS,
  pubSearchListingTypeForTab,
  listingMatchesSearchListingType,
} from '../../util/searchListingTabs';
import { isFirstBookingDiscountEligible } from '../../util/firstBookingDiscount';
import { getWeekdayBookingDiscountPercent, isWeekdayYmd } from '../../util/weekdayBookingDiscount';
import {
  cardHourlyPrice,
  cardTotalSubunits,
  searchDurationHours,
} from '../../util/listingCardPrice';
import { getCustomerFacingPriceSubunitsAndCurrency } from '../../util/cocopoolPricing';
import { parse } from '../../util/urlHelpers';
import { extractProvinceFromAddress } from '../../util/locationDisplay';

import { withoutSquareVariantsOnListingImages } from '../../util/imageVariants';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  saveSearchHistory,
  getProvinceOccupancy,
  getSeoLandingPages,
  searchMirrorListings,
  reportMirrorShadow,
} from '../../util/api';
import { seoCityOfAddress, seoLandingCanonicalPath } from '../../util/seoLandings';
import { addPendingSearch } from '../../util/pendingSearches';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns; 24 is divisible by 2, 3, and 4
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 24;

// Sharetribe Flex rejects the `page` parameter when the availability filter is in use
// (400 "Pagination is not supported for this query."), capping each query at
// DATES_PER_PAGE. To recover more results on dense bounded searches we split the
// bounds into 4 quadrants and fire one sub-query per quadrant in parallel, then
// merge and dedupe the responses.
const DATES_PER_PAGE = 100;

// When the user picks a date but no time, the search window defaults to the whole
// day (00:00–18:00). Requiring that entire window free would hide almost every pool,
// so date-only searches only require a realistic booking block (4h) free that day.
const DATE_ONLY_MIN_DURATION_MINUTES = 240;

/**
 * En la pestaña Todos, el orden de los grupos depende de la temporada de la fecha
 * buscada (mayo–septiembre), o de hoy si no hay fecha (marzo–septiembre; `isPoolSeasonFor`):
 *
 * - **Temporada de piscina**: piscinas → jardines → interiores.
 * - **Fuera de temporada**: jardines e interiores **intercalados** uno a uno (jardín,
 *   interior, jardín…), cada tipo en su propio orden de distancia (o del sort elegido);
 *   cuando uno se acaba sigue el otro. Para una celebración en invierno los dos sirven,
 *   y la lista tiene que enseñar los dos. No se mezclan por distancia pura: en ciudad
 *   los interiores están en el centro y los jardines en las afueras, y en Barcelona eso
 *   llenaba las primeras páginas de interiores. Una piscina pelada es lo menos útil de
 *   la lista y va al final.
 *
 * El grupo de cada anuncio es la cara que enseña su tarjeta (`resolveListingFace` con
 * esa fecha): una piscina con jardín cuenta como jardín fuera de temporada. Interiores
 * van aparte aunque su única cara sea la base (`POOL_FACE`). Partición estable; se
 * aplica entre el sort y el reordenado de superanfitriones.
 *
 * @param {Object} response respuesta del SDK
 * @param {string} [dateYmd] primera fecha buscada, `YYYY-MM-DD`
 */
export const applyTodosTypePriority = (response, dateYmd) => {
  const listings = response?.data?.data || [];
  if (listings.length < 2) {
    return response;
  }
  const pools = [];
  const gardens = [];
  const indoors = [];
  listings.forEach(l => {
    if (isInterioresListing(l)) {
      indoors.push(l);
    } else if (resolveListingFace({ listing: l, dateYmd }) === GARDEN_FACE) {
      gardens.push(l);
    } else {
      pools.push(l);
    }
  });
  const interleaved = [];
  for (let i = 0; i < Math.max(gardens.length, indoors.length); i++) {
    if (i < gardens.length) interleaved.push(gardens[i]);
    if (i < indoors.length) interleaved.push(indoors[i]);
  }
  const ordered = isPoolSeasonFor(dateYmd)
    ? [...pools, ...gardens, ...indoors]
    : [...interleaved, ...pools];
  return {
    ...response,
    data: { ...response.data, data: ordered },
  };
};

// To give superhosts global priority we fetch the whole result set client-side and
// reorder it before paginating (Flex paginates server-side, so a superhost on page 3
// would otherwise stay on page 3). SUPERHOST_FETCH_PER_PAGE is the max page size Flex
// allows; SUPERHOST_MAX_FETCH_PAGES caps the number of round-trips for huge result
// sets so a single search can never fan out unbounded.
const SUPERHOST_FETCH_PER_PAGE = 100;
const SUPERHOST_MAX_FETCH_PAGES = 10;

// Where searches without dates come from (docs/mirror-search.md), `REACT_APP_MIRROR_SEARCH_MODE`:
// - off (default): Sharetribe, as always.
// - shadow: Sharetribe, and the browser also asks the mirror and reports any difference.
// - on: the mirror; Sharetribe only when the mirror cannot answer. MIRROR_WATCH_RATE of the
//   searches keep comparing in the background (that sample does cost credits).
export const MIRROR_OFF = 'off';
export const MIRROR_SHADOW = 'shadow';
export const MIRROR_ON = 'on';
export const MIRROR_WATCH_RATE = 0.01;
export const mirrorModeOf = mode =>
  mode === MIRROR_SHADOW || mode === MIRROR_ON ? mode : MIRROR_OFF;
const modeOf = mirrorModeOf;
export const mirrorSearchMode = () => modeOf(process.env.REACT_APP_MIRROR_SEARCH_MODE);
// Searches with dates have their own switch, `REACT_APP_MIRROR_DATES_MODE` (same values), so they
// can go through shadow while the ones without dates are already on. The mirror answers them from
// the free time slots Sharetribe itself computes (calendar, exceptions, bookings, seats).
export const mirrorDatesMode = () => modeOf(process.env.REACT_APP_MIRROR_DATES_MODE);

// JSON with sorted keys: Sharetribe and the mirror (jsonb) do not keep keys in the same order.
const stableStringify = value => {
  if (value === null || typeof value !== 'object' || value instanceof Date) {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  return `{${Object.keys(value)
    .sort()
    .map(k => `${JSON.stringify(k)}:${stableStringify(value[k])}`)
    .join(',')}}`;
};

/**
 * Compares two `listings.query` responses for shadow mode: which listings one has and the other
 * does not, and which common ones differ (attributes, photos or their author's profile). Only
 * the set matters, not the order: the browser re-sorts the whole result itself.
 */
export const compareSearchResults = (sharetribeResponse, mirrorResponse) => {
  const index = response => {
    const users = new Map(
      (response?.data?.included || [])
        .filter(e => e.type === 'user')
        .map(u => [u.id?.uuid, stableStringify(u.attributes)])
    );
    return new Map(
      (response?.data?.data || []).map(l => {
        const authorId = l.relationships?.author?.data?.id?.uuid;
        const imageIds = (l.relationships?.images?.data || []).map(i => i.id?.uuid);
        const fingerprint = stableStringify({
          attributes: l.attributes,
          authorId,
          author: users.get(authorId) || null,
          imageIds,
        });
        return [l.id?.uuid, fingerprint];
      })
    );
  };
  const fromSharetribe = index(sharetribeResponse);
  const fromMirror = index(mirrorResponse);
  const missing = [...fromSharetribe.keys()].filter(id => !fromMirror.has(id));
  const extra = [...fromMirror.keys()].filter(id => !fromSharetribe.has(id));
  const changed = [...fromSharetribe.keys()].filter(
    id => fromMirror.has(id) && fromMirror.get(id) !== fromSharetribe.get(id)
  );
  return {
    same: missing.length === 0 && extra.length === 0 && changed.length === 0,
    sharetribe: fromSharetribe.size,
    mirror: fromMirror.size,
    missing,
    extra,
    changed,
  };
};

/**
 * La última búsqueda que se grabó en el perfil, para no grabarla otra vez.
 *
 * ESTO ERA UN FALLO CARO. La comparación anterior miraba
 * `state.SearchPage.searchParams.address`, y `address` **nunca llega ahí**: `loadData` lo
 * extrae de los parámetros (`const { page, address, origin, sort, ...rest }`) y no lo pasa a
 * `searchListings`, porque Flex no lo entiende. Así que `prev.address` era siempre
 * `undefined`, `address !== prev.address` siempre cierto, y **toda carga de la página de
 * búsqueda de un usuario con sesión escribía su perfil**: `currentUser.show` + `users.show`
 * + `users.updateProfile`, 12 créditos cada vez. Medido el 26-10-2026: 7.566 escrituras al
 * día, ~2,6M de créditos al mes, el 18% de la factura. En los datos se veían nueve búsquedas
 * idénticas separadas por segundos.
 *
 * Vive en el módulo y no en el estado a propósito: esto solo corre en el navegador (está
 * dentro de `typeof window !== 'undefined'`) y no es estado de la aplicación, es memoria de
 * lo último que se mandó. Al recargar la página se pierde, y entonces vuelve a grabarse una
 * vez — por eso el servidor tiene su propia guarda, que es la que de verdad cierra el agujero
 * (`server/api/saveSearchHistory.js`).
 */
let lastRecordedSearchKey = null;

// Time zone used to interpret the `dates`/`time` search params. Must match the one
// used in `datesSearchParams` below so the "hours until start" math is consistent.
const SEARCH_TZ = 'Europe/Madrid';

// Resolve the booking start instant from the URL `dates` + `time` params, or null
// when no valid date search is active.
const parseSearchStartInstant = (datesParam, timesParam) => {
  const values = typeof datesParam === 'string' ? datesParam.split(',') : [];
  if (values.length !== 2) {
    return null;
  }
  const timeValues = typeof timesParam === 'string' ? timesParam.split(',') : [];
  const startTime = timeValues.length === 2 ? timeValues[0] : '00:00';
  return parseDateFromISO8601(values[0], startTime, SEARCH_TZ);
};

// Resolve the booked duration in hours from the URL `dates` + `time` params, or null
// when the search doesn't constrain duration (no valid dates, or no explicit time range —
// in which case the user hasn't asked for a specific length and we must not filter).
// Replace a response's listings with a filtered subset and realign the pagination meta.
// Only rewrites the totals when the full result set is in hand (date searches fetch every
// match in one page). When the server is paginating across multiple pages, keep its meta
// untouched so the pagination controls don't disappear.
const rebuildResponseAfterFilter = (response, filtered) => {
  const data = response?.data?.data || [];
  if (filtered.length === data.length) {
    return response;
  }

  const meta = response?.data?.meta || {};
  const hasAllResults = meta.totalItems == null || meta.totalItems <= data.length;
  const newMeta = hasAllResults
    ? {
        ...meta,
        totalItems: filtered.length,
        totalPages: Math.max(1, Math.ceil(filtered.length / (meta.perPage || RESULT_PAGE_SIZE))),
        // Realign Flex's stale `paginationLimit` with the recomputed page count so the
        // pagination controls aren't capped below totalPages. See applySuperhostSearchOrdering.
        paginationLimit: Math.max(
          1,
          Math.ceil(filtered.length / (meta.perPage || RESULT_PAGE_SIZE))
        ),
      }
    : meta;

  return {
    ...response,
    data: { ...response.data, data: filtered, meta: newMeta },
  };
};

// Drop listings whose provider-configured minimum booking notice (publicData.minAdvanceHours)
// is greater than the number of whole hours between "now" and the searched booking start.
// Listings without the field (or with an invalid value) are treated as 0 (no restriction),
// so they always remain visible. Only applied when a date/time search is active.
const filterResponseByAdvanceNotice = (response, datesParam, timesParam) => {
  const startDate = parseSearchStartInstant(datesParam, timesParam);
  const data = response?.data?.data;
  if (!startDate || !Array.isArray(data)) {
    return response;
  }

  const hoursUntilStart = (startDate.getTime() - Date.now()) / (1000 * 60 * 60);
  const filtered = data.filter(listing => {
    const raw = parseInt(listing?.attributes?.publicData?.minAdvanceHours, 10);
    const minAdvanceHours = Number.isFinite(raw) ? raw : 0;
    return minAdvanceHours <= hoursUntilStart;
  });

  return rebuildResponseAfterFilter(response, filtered);
};

// Drop listings whose provider-configured minimum booking length (publicData.minimumBookingHours)
// is greater than the searched booking duration. The availability filter (time-partial +
// minDuration) only guarantees the window is free, not that the host accepts a booking that
// short, so e.g. a listing with a 6h minimum would otherwise show up for a 4h search.
// Listings without the field (or with an invalid value) are treated as 0 (no restriction).
export const filterResponseByMinBookingDuration = (response, datesParam, timesParam) => {
  const durationHours = searchDurationHours(datesParam, timesParam);
  const data = response?.data?.data;
  if (durationHours == null || !Array.isArray(data)) {
    return response;
  }

  const filtered = data.filter(listing => {
    const raw = parseInt(listing?.attributes?.publicData?.minimumBookingHours, 10);
    const minBookingHours = Number.isFinite(raw) ? raw : 0;
    return minBookingHours <= durationHours;
  });

  return rebuildResponseAfterFilter(response, filtered);
};

// "Con descuento" filter: keep only listings that advertise a discount — the 20%
// first-booking welcome offer or a weekday (Mon–Fri) discount. It runs client-side, like the
// two filters above, for two reasons: Flex can't OR across two extended-data fields, and
// first-booking eligibility isn't a listing attribute at all (it depends on the author's
// profile, which travels in the response `included`).
// Note the weekday part ignores the searched dates on purpose: the listing offers the
// discount either way, and the card keeps showing its badge on weekend searches.
export const filterResponseByDiscount = response => {
  const data = response?.data?.data;
  if (!Array.isArray(data)) {
    return response;
  }

  const authorsById = new Map(
    (response.data.included || [])
      .filter(entity => entity?.type === 'user' && entity.id?.uuid)
      .map(entity => [entity.id.uuid, entity])
  );

  const filtered = data.filter(listing => {
    const publicData = listing?.attributes?.publicData;
    if (getWeekdayBookingDiscountPercent(publicData) > 0) {
      return true;
    }
    const authorId = listing?.relationships?.author?.data?.id?.uuid;
    return isFirstBookingDiscountEligible(publicData, authorsById.get(authorId));
  });

  return rebuildResponseAfterFilter(response, filtered);
};

/**
 * Total de la reserva buscada de cada anuncio (subunidades), tal y como lo enseña su tarjeta: la
 * cara que muestra (misma resolución que filterResponseByFaceCapacity), el precio del tramo del
 * grupo con la comisión de cliente y el descuento si la tarjeta lo aplica, × las horas
 * buscadas. Ver cardHourlyPrice en util/listingCardPrice.js. Sin precio → null.
 */
export const listingTotalsById = (
  response,
  { searchListingType, dateYmd, capacity, hours, isWeekday }
) => {
  const authorsById = new Map(
    (response?.data?.included || [])
      .filter(entity => entity?.type === 'user' && entity.id?.uuid)
      .map(entity => [entity.id.uuid, entity])
  );
  const faceParam = FACE_FOR_SEARCH_LISTING_TYPE[searchListingType];
  const totals = new Map();
  (response?.data?.data || []).forEach(listing => {
    const face = resolveListingFace({ listing, faceParam, dateYmd });
    const authorId = listing?.relationships?.author?.data?.id?.uuid;
    const hourly = cardHourlyPrice({
      listing: applyFaceToListing(listing, face),
      author: authorsById.get(authorId),
      capacity,
      isWeekday,
    });
    totals.set(listing.id?.uuid, cardTotalSubunits(hourly, hours));
  });
  return totals;
};

/**
 * Filtro "Precio total" (`price=min,max`, en euros): deja los anuncios cuyo total está en el
 * rango. Flex solo sabe filtrar por `attributes.price` (base por hora, sin comisión ni tramo),
 * así que se hace aquí sobre el conjunto completo. Un anuncio sin precio no entra.
 */
export const filterResponseByTotalPrice = (
  response,
  priceParam,
  totalsById,
  currency,
  capUnits
) => {
  const [min, max] = String(priceParam || '')
    .split(',')
    .map(Number);
  const data = response?.data?.data;
  if (!Array.isArray(data) || !Number.isFinite(min) || !Number.isFinite(max)) {
    return response;
  }
  const divisor = unitDivisor(currency);
  // El tope del slider es "o más": con el máximo ahí, no hay límite superior.
  const upper = capUnits != null && max >= capUnits ? Infinity : max * divisor;
  const filtered = data.filter(listing => {
    const total = totalsById.get(listing.id?.uuid);
    return total != null && total >= min * divisor && total <= upper;
  });
  return rebuildResponseAfterFilter(response, filtered);
};

// Barras del histograma de "Precio total".
export const TOTAL_PRICE_HISTOGRAM_BINS = 40;

// Percentil del tope del slider: por encima, "o más". Con el total más alto como tope, un solo
// anuncio carísimo aplastaba el resto del histograma contra la izquierda.
const TOTAL_PRICE_CAP_PERCENTILE = 0.9;

/**
 * Datos del slider de "Precio total", en `meta` para que la página los lea junto a la
 * paginación: el tope (`totalPriceMax`, el percentil 90 de los totales redondeado hacia arriba
 * a la decena; el slider lo muestra como "640 €+") y el histograma (cuántos anuncios caen en
 * cada tramo de [0, tope]; la última barra recoge también todo lo que lo supera).
 */
export const withTotalPriceRange = (response, totalsById, currency) => {
  const divisor = unitDivisor(currency);
  const totals = Array.from(totalsById.values())
    .filter(t => t != null)
    .map(t => t / divisor);
  if (totals.length === 0 || !response?.data?.meta) {
    return response;
  }
  const sorted = [...totals].sort((a, b) => a - b);
  const capValue =
    sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * TOTAL_PRICE_CAP_PERCENTILE))];
  const maxUnits = Math.max(10, Math.ceil(capValue / 10) * 10);
  const histogram = new Array(TOTAL_PRICE_HISTOGRAM_BINS).fill(0);
  totals.forEach(t => {
    const bin = Math.min(
      TOTAL_PRICE_HISTOGRAM_BINS - 1,
      Math.floor((t / maxUnits) * TOTAL_PRICE_HISTOGRAM_BINS)
    );
    histogram[bin] += 1;
  });
  return {
    ...response,
    data: {
      ...response.data,
      meta: { ...response.data.meta, totalPriceMax: maxUnits, totalPriceHistogram: histogram },
    },
  };
};

/**
 * Category-tab filter (pool / garden / interiores). Also sent to Flex as
 * `pub_searchListingType`; production filters on it, but the dev marketplace ignores
 * it (every value returns the full universe) — so we enforce the tab client-side
 * over the full result set, same pattern as the discount filter.
 */
export const filterResponseBySearchListingType = (response, searchListingType) => {
  if (!searchListingType) {
    return response;
  }
  const data = response?.data?.data;
  if (!Array.isArray(data)) {
    return response;
  }
  const filtered = data.filter(listing =>
    listingMatchesSearchListingType(listing, searchListingType)
  );
  return rebuildResponseAfterFilter(response, filtered);
};

/**
 * Chips "Apto para lluvia" / "Con espacio interior": red de seguridad en cliente, como la
 * de la pestaña. Flex **ignora en silencio** un `pub_` sin índice de búsqueda (devuelve todo,
 * no un error), así que sin el índice de `weatherProtectedFor` / `hasIndoorSpace` la query no
 * filtraría nada. Con los dos chips activos, el anuncio tiene que cumplir ambos.
 */
export const filterResponseByShelterChips = (response, { rain, indoor } = {}) => {
  const data = response?.data?.data;
  if (!Array.isArray(data)) {
    return response;
  }
  const filtered = data.filter(
    listing => (!rain || isRainProofListing(listing)) && (!indoor || hasIndoorSpaceListing(listing))
  );
  return rebuildResponseAfterFilter(response, filtered);
};

// Cara que cada pestaña tipada promete; las demás (Todos, interiores) no filtran por cara.
const FACE_FOR_SEARCH_LISTING_TYPE = { pool: POOL_FACE, garden: GARDEN_FACE };

/**
 * Pestañas de piscinas y jardines con fecha: un anuncio con las dos caras sale solo en
 * la pestaña de la cara con la que ese día se alquila (`resolveListingFace`: sus
 * `poolMonths`, o la ventana de verano global). En diciembre sale en Jardines y no en
 * Piscinas; en julio, al revés. Sin fecha no se filtra. Todos los enseña siempre.
 */
export const filterResponseByDateFace = (response, searchListingType, dateYmd) => {
  const tabFace = FACE_FOR_SEARCH_LISTING_TYPE[searchListingType];
  if (!tabFace || !dateYmd) {
    return response;
  }
  const data = response?.data?.data;
  if (!Array.isArray(data)) {
    return response;
  }
  const filtered = data.filter(listing => {
    if (resolveListingFace({ listing, dateYmd }) === tabFace) {
      return true;
    }
    // Piscinas keeps a dual listing that is out of its pool season but whose host lets
    // guests use the pool anyway (`gardenPoolUseExtra`): the pool IS available that day.
    // The tab is an explicit pool request, so its card shows the pool face and price.
    return tabFace === POOL_FACE && offersPoolUseExtra(listing, GARDEN_FACE);
  });
  return rebuildResponseAfterFilter(response, filtered);
};

/**
 * Filtro de grupo (`pub_capacity`) contra los tramos de la cara que enseña la tarjeta.
 *
 * Flex filtra por `publicData.capacity`, que es uno por anuncio y sale de los tramos de
 * la piscina. La cara de jardín tiene los suyos (`gardenPriceRange`) y pueden llegar a
 * menos bañistas: sin esto, una piscina con jardín de 1-50 bañistas en piscina y 1-30 en
 * jardín salía en Jardines para "31-50", con la tarjeta cayendo al precio "desde" de un
 * jardín que no se puede reservar para ese grupo. Se resuelve por intervalos
 * (`resolvePriceRangeRowForGuestRange`), igual que el precio de la tarjeta; un anuncio sin
 * tramos se deja pasar, porque no hay nada contra lo que comprobarlo.
 *
 * La cara se decide como en `ListingCard`: pestaña de piscinas → piscina, de jardines →
 * jardín, Todos → la de la fecha.
 */
export const filterResponseByFaceCapacity = (
  response,
  capacityParam,
  searchListingType,
  dateYmd
) => {
  const buckets = capacityParam
    ? parseSelectFilterOptions(String(capacityParam))
        .map(b => b.trim())
        .filter(Boolean)
    : [];
  if (buckets.length === 0) {
    return response;
  }
  const data = response?.data?.data;
  if (!Array.isArray(data)) {
    return response;
  }
  const faceParam = FACE_FOR_SEARCH_LISTING_TYPE[searchListingType];
  const filtered = data.filter(listing => {
    const face = resolveListingFace({ listing, faceParam, dateYmd });
    const { priceRange } = faceView(listing, face);
    if (!Array.isArray(priceRange) || priceRange.length === 0) {
      return true;
    }
    return buckets.some(bucket => resolvePriceRangeRowForGuestRange(priceRange, bucket));
  });
  return rebuildResponseAfterFilter(response, filtered);
};

// After merging quadrant responses, the listings are no longer in a single global
// sort order (each sub-query was sorted independently). Re-sort client-side using
// the same field/direction semantics as Sharetribe Flex's `sort` parameter, or by
// distance from origin when no sort is specified (Flex's default behavior).
// "Más valoradas" sorts by the host's rating, which lives on the author profile (carried in the
// response `included`), not on the listing — so Flex can't sort it and we order client-side.
const RATINGS_SORT_KEY = 'meta_rating';

const buildAuthorRatingLookup = included => {
  const map = new Map();
  (included || []).forEach(entity => {
    if (entity?.type === 'user' && entity.id?.uuid) {
      const pd = entity.attributes?.profile?.publicData || {};
      const avg = Number(pd.averageRating);
      const total = Number(pd.totalReviews);
      map.set(entity.id.uuid, {
        averageRating: Number.isFinite(avg) ? avg : null,
        totalReviews: Number.isFinite(total) ? total : null,
      });
    }
  });
  return map;
};

const authorRatingOf = (listing, lookup) => {
  const authorId = listing?.relationships?.author?.data?.id?.uuid;
  return (authorId && lookup.get(authorId)) || { averageRating: null, totalReviews: null };
};

const extractSortValue = (listing, field, capacity) => {
  if (field === 'createdAt') {
    const v = listing.attributes?.createdAt;
    return v ? new Date(v).getTime() : null;
  }
  if (field === 'price') {
    // Use the capacity-adjusted, customer-facing price (the one shown on the card) so the order
    // matches what the user sees — in particular when a capacity (group size) filter is active.
    const cf = getCustomerFacingPriceSubunitsAndCurrency(listing, capacity);
    return cf?.amount ?? listing.attributes?.price?.amount ?? null;
  }
  if (field.startsWith('pub_')) {
    return listing.attributes?.publicData?.[field.slice(4)] ?? null;
  }
  return null;
};

// Squared lat/lng delta — monotonic with true distance for city-scale areas,
// which is enough for sort ordering and avoids the cost of haversine.
const squaredDistanceFromOrigin = (geolocation, origin) => {
  if (!geolocation || !origin) return null;
  const dLat = geolocation.lat - origin.lat;
  const dLng = geolocation.lng - origin.lng;
  return dLat * dLat + dLng * dLng;
};

const compareNullable = (va, vb, descending) => {
  if (va == null && vb == null) return 0;
  if (va == null) return 1;
  if (vb == null) return -1;
  if (va === vb) return 0;
  return descending ? (va < vb ? 1 : -1) : va < vb ? -1 : 1;
};

const sortMergedListings = (response, sortKey, origin, capacity) => {
  if (!response) return response;

  const listings = response.data?.data || [];

  // Ratings: order by the host's number of reviews (totalReviews, desc), tie-breaking by
  // averageRating so a host with a higher average ranks higher at the same review count.
  // Listings without any reviews sort last.
  if (sortKey === RATINGS_SORT_KEY) {
    const lookup = buildAuthorRatingLookup(response.data?.included);
    const sorted = [...listings].sort((a, b) => {
      const ra = authorRatingOf(a, lookup);
      const rb = authorRatingOf(b, lookup);
      const byTotal = compareNullable(ra.totalReviews, rb.totalReviews, true);
      return byTotal !== 0 ? byTotal : compareNullable(ra.averageRating, rb.averageRating, true);
    });
    return { ...response, data: { ...response.data, data: sorted } };
  }

  const isRelevanceOrDefault = !sortKey || sortKey === 'relevance';

  let sorted;
  if (isRelevanceOrDefault) {
    if (!origin) return response;
    sorted = [...listings].sort((a, b) =>
      compareNullable(
        squaredDistanceFromOrigin(a.attributes?.geolocation, origin),
        squaredDistanceFromOrigin(b.attributes?.geolocation, origin),
        false
      )
    );
  } else {
    const isDescending = sortKey.startsWith('-');
    const field = isDescending ? sortKey.slice(1) : sortKey;
    sorted = [...listings].sort((a, b) =>
      compareNullable(
        extractSortValue(a, field, capacity),
        extractSortValue(b, field, capacity),
        isDescending
      )
    );
  }

  return { ...response, data: { ...response.data, data: sorted } };
};

export const applySuperhostSearchOrdering = (
  response,
  { sort, origin, pageParam, capacity, prioritize = r => r }
) => {
  if (!response?.data?.data?.length) {
    return response;
  }

  // `prioritize` (la prioridad por cara) va entre el sort y los superanfitriones: antes
  // del sort se perdía, porque el sort reordena la lista entera y solo respeta empates.
  const sorted = prioritize(sortMergedListings(response, sort, origin, capacity));
  const orderedData = reorderListingsForSuperhostsAcrossPages(
    sorted.data?.data || [],
    RESULT_PAGE_SIZE,
    undefined,
    makeRawSuperhostPredicate(sorted.data?.included)
  );
  const totalItems = orderedData.length;
  const currentPage = parseInt(pageParam, 10) || 1;
  const totalPages = Math.max(1, Math.ceil(totalItems / RESULT_PAGE_SIZE));

  return {
    ...sorted,
    data: {
      ...sorted.data,
      data: orderedData,
      meta: {
        ...sorted.data.meta,
        page: currentPage,
        perPage: RESULT_PAGE_SIZE,
        totalItems,
        totalPages,
        // Once the full result set is in memory we paginate client-side, so Flex's
        // `paginationLimit` (computed for the larger server perPage, e.g. 100) no longer
        // applies. Leaving it stale would make PaginationLinks cap the navigable pages
        // below totalPages (the "only 2 pages" bug). Realign it with the client totalPages.
        paginationLimit: totalPages,
        paginationUnsupported: false,
      },
    },
  };
};

const mergeSdkListingResponses = responses => {
  const valid = responses.filter(Boolean);
  if (valid.length === 0) return null;
  if (valid.length === 1) return valid[0];

  const first = valid[0];
  const seenListings = new Set();
  const data = [];
  const seenIncluded = new Set();
  const included = [];

  valid.forEach(r => {
    (r.data?.data || []).forEach(l => {
      const id = l.id?.uuid;
      if (id && !seenListings.has(id)) {
        seenListings.add(id);
        data.push(l);
      }
    });
    (r.data?.included || []).forEach(inc => {
      const key = `${inc.type}:${inc.id?.uuid}`;
      if (!seenIncluded.has(key)) {
        seenIncluded.add(key);
        included.push(inc);
      }
    });
  });

  return {
    ...first,
    data: {
      ...first.data,
      data,
      included,
      meta: {
        ...first.data.meta,
        page: 1,
        perPage: data.length,
        totalItems: data.length,
        totalPages: 1,
        // Drop Flex's stale `paginationLimit` from the source page (computed for the
        // server perPage); the merged set is paginated client-side. See applySuperhostSearchOrdering.
        paginationLimit: 1,
      },
    },
  };
};

// Una respuesta sin anuncios, con la forma de la del SDK. Para cuando un filtro deja
// todas sus queries fuera: devolver la primera (o null) enseñaría resultados sin filtrar.
const emptyListingsResponse = () => ({
  data: {
    data: [],
    included: [],
    meta: { page: 1, perPage: 0, totalItems: 0, totalPages: 1, paginationLimit: 1 },
  },
});

/**
 * Chips "Apto para lluvia" / "Con espacio interior". Cada chip es un OR de campos distintos, y
 * Flex no sabe hacer OR entre campos, así que se lanza una query por alternativa y se fusionan
 * por id (como la pestaña de jardines):
 * - lluvia: `pub_weatherProtectedFor` | "Zona techada" en `pub_extras` | tipo interiores;
 * - interior: `pub_hasIndoorSpace` | "Comedor interior" o "Salón interior" en `pub_extras` |
 *   tipo interiores.
 * Con los dos chips es el producto de ambas listas. Interiores cumple los dos chips por sí
 * solo, así que basta su query, y solo existe en Todos: en Piscinas y Jardines no cabe, y en
 * Interiores el filtro no cambia nada (lo resuelve el llamador). Las queries solo acotan lo
 * que se descarga: quien decide es filterResponseByShelterChips. Ver docs/search-filters.md.
 */
export const shelterSearchQueries = (queryParams, { rain, indoor } = {}) => {
  const interiores = { pub_searchListingType: 'interiores' };
  const canAddInteriores = queryParams.pub_searchListingType == null;
  // Si ya hay un filtro de Extras (Barbacoa…), no se pisa: esa query descarga de más y el
  // filtro de cliente decide.
  // Un espacio interior también resguarda de la lluvia: "Comedor interior" / "Salón interior"
  // van en la misma query que "Zona techada" (mismo campo) y los de la pestaña Espacios en la
  // de `hasIndoorSpace`.
  const coveredArea =
    queryParams.pub_extras == null
      ? { pub_extras: `has_any:${[COVERED_AREA_EXTRA, ...INDOOR_SPACE_EXTRAS].join(',')}` }
      : {};
  const rainAlternatives = [
    { pub_weatherProtectedFor: 'has_any:rain' },
    coveredArea,
    { pub_hasIndoorSpace: true },
  ];
  // Mismo campo que "Zona techada" y que Barbacoa: si la URL ya filtra Extras, no se pisa.
  const indoorExtras =
    queryParams.pub_extras == null
      ? { pub_extras: `has_any:${INDOOR_SPACE_EXTRAS.join(',')}` }
      : {};
  const indoorAlternatives = [{ pub_hasIndoorSpace: true }, indoorExtras];

  const combos = [{}];
  [rain && rainAlternatives, indoor && indoorAlternatives].filter(Boolean).forEach(alternatives => {
    const current = combos.splice(0);
    current.forEach(c => alternatives.forEach(a => combos.push({ ...c, ...a })));
  });
  const queries = combos.map(c => ({ ...queryParams, ...c }));
  if (canAddInteriores) {
    queries.push({ ...queryParams, ...interiores });
  }
  // Las alternativas pueden coincidir (p. ej. Extras ya filtrado): sin duplicados.
  const seen = new Set();
  return queries.filter(q => {
    const key = JSON.stringify(q);
    return seen.has(key) ? false : seen.add(key);
  });
};

// Combine the two relevance (pub_score) queries into a single result set: scored listings
// first (ranked by score, from the score-sorted query), then the remaining listings ordered
// by distance (from the origin query). Listings present in both are deduped by id, keeping
// the scored position. Mirrors the merge the former /searchListings server proxy performed.
export const mergeRelevanceResults = (scoredResp, distanceResp) => {
  const scored = (scoredResp?.data?.data || []).filter(
    l => l?.attributes?.publicData?.score != null
  );
  const unscored = (distanceResp?.data?.data || []).filter(
    l => l?.attributes?.publicData?.score == null
  );
  return (
    mergeSdkListingResponses([
      { ...scoredResp, data: { ...scoredResp.data, data: scored } },
      { ...distanceResp, data: { ...distanceResp.data, data: unscored } },
    ]) || scoredResp
  );
};

export const buildSearchListingsSdkFields = config => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
    config?.layout?.listingImage || {};
  const aspectRatio = aspectHeight / aspectWidth;
  return {
    include: ['author', 'images', 'author.profile', 'author.profileImage'],
    'fields.listing': [
      'title',
      'geolocation',
      'price',
      'createdAt',
      'publicData.score',
      'publicData.capacity',
      'publicData.capacityRanges',
      'publicData.extras',
      'publicData.priceRange',
      'publicData.searchListingType',
      // Caras piscina / jardín. La descripción no hace falta: la tarjeta no la pinta.
      // `category` es la clave antigua del tipo de espacio y aquí decide si un anuncio
      // "ya es" jardín, así que sin ella la prioridad estacional se equivocaría.
      'publicData.category',
      'publicData.hasGarden',
      // Chips de lluvia e interior: sin los campos, filterResponseByShelterChips lo
      // descartaría todo (el de interior también lee `extras`, ya arriba).
      'publicData.hasIndoorSpace',
      'publicData.weatherProtectedFor',
      // Espacios interiores: etiqueta "Con espacio interior +X €" de la tarjeta y, con el chip,
      // sus fotos primero y el enlace con el espacio ya añadido (ListingCard).
      'publicData.indoorSpaces',
      // Meses de piscina del anfitrión: sin ellos la cara por fecha caería siempre a la
      // ventana de verano global y el filtro de Piscinas se equivocaría con esos anuncios.
      'publicData.poolMonths',
      // Cara piscina apagada por ops (dashboard de migración): la tarjeta y la pestaña
      // Piscinas tienen que saberlo o enseñarían una piscina que no se ofrece.
      'publicData.poolOffered',
      // Piscinas conserva, fuera de temporada, los anuncios cuyo anfitrión ofrece el uso
      // de la piscina (filterResponseByDateFace): sin este campo el filtro los tiraba.
      'publicData.gardenPoolUseExtra',
      'publicData.gardenTitle',
      'publicData.gardenPriceRange',
      'publicData.gardenImageOrder',
      'publicData.hiddenImageIds',
      'publicData.listingType',
      'publicData.unitType',
      'publicData.maxGuest',
      'publicData.minGuest',
      'publicData.minimumBookingHours',
      'publicData.minAdvanceHours',
      'publicData.privacy',
      'publicData.poolMaintenance',
      'publicData.bathrooms',
      'publicData.ownerPresent',
      'publicData.transactionProcessAlias',
      'publicData.city',
      // La tarjeta pone "municipio, provincia" (la provincia solo está en parte de los anuncios).
      'publicData.province',
      // "Apto para lluvia (30 personas)": la capacidad declarada para la lluvia.
      'publicData.weatherProtection',
      'publicData.firstBookingDiscountEnabled',
      'publicData.weekdayBookingDiscountPercent',
      'publicData.tag',
    ],
    'fields.user': [
      'profile.displayName',
      'profile.abbreviatedName',
      'profile.publicData.averageRating',
      'profile.publicData.totalReviews',
      'profile.publicData.hasReceivedBooking',
      'profile.publicData.firstBookingDiscountOverride',
    ],
    'fields.image': [
      // Nada de `scaled-*` aquí: las tarjetas y la tarjeta del mapa filtran por
      // `variantPrefix`, así que esas dos URLs viajaban en cada resultado sin que nadie
      // las dibujara. Y una URL que nadie dibuja no es gratis: la primera vez que un
      // cliente (Googlebot, sin ir más lejos) tira de ella, esa foto cuesta 340 créditos
      // ese mes. Las `scaled-*` solo las usa SectionHero, en la ficha, que ahora cae a las de
      // la tarjeta si no están.
      // Host avatar variants for the superhost badge on the card.
      'variants.square-small',
      'variants.square-small2x',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(variantPrefix, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };
};

// ================ Action types ================ //

export const SEARCH_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_LISTINGS_REQUEST';
export const SEARCH_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/SearchPage/SEARCH_LISTINGS_ERROR';

export const SEARCH_MAP_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_MAP_LISTINGS_REQUEST';
export const SEARCH_MAP_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_MAP_LISTINGS_SUCCESS';
export const SEARCH_MAP_LISTINGS_ERROR = 'app/SearchPage/SEARCH_MAP_LISTINGS_ERROR';

export const SEARCH_MAP_SET_ACTIVE_LISTING = 'app/SearchPage/SEARCH_MAP_SET_ACTIVE_LISTING';

export const FETCH_REVIEWS_REQUEST = 'app/SearchPage/FETCH_REVIEWS_REQUEST';
export const FETCH_REVIEWS_SUCCESS = 'app/SearchPage/FETCH_REVIEWS_SUCCESS';
export const FETCH_REVIEWS_ERROR = 'app/SearchPage/FETCH_REVIEWS_ERROR';

export const SET_OCCUPANCY_PERCENTAGE = 'app/SearchPage/SET_OCCUPANCY_PERCENTAGE';

export const SET_SEO_CANONICAL_PATH = 'app/SearchPage/SET_SEO_CANONICAL_PATH';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  searchParams: null,
  searchInProgress: false,
  searchListingsError: null,
  currentPageResultIds: [],
  reviews: [],
  fetchReviewsError: null,
  reviewsInRequest: false,
  // Province occupancy (0–100) for the searched date, driving the "high demand"
  // banner. null = unknown / no date / below threshold → no banner.
  occupancyPercentage: null,
  // Landing SEO que hace de canonical de esta búsqueda (`/barcelona/piscinas`), o null si no
  // hay ninguna y la búsqueda se queda con el suyo. Ver docs/seo-landings.md.
  seoCanonicalPath: null,
};

const resultIds = data => data.data.map(l => l.id);

// A new page or a different category tab replaces the results outright; anything else
// (the client re-run of loadData after SSR, a map move, a filter) keeps them on screen
// until the new ones land. Paging with the old page still visible reads as "it loaded
// page 1 and then the right one".
const replacesVisibleResults = (prevParams, nextParams) => {
  if (!prevParams) {
    return false;
  }
  const pageOf = params => parseInt(params?.page, 10) || 1;
  return (
    prevParams.pub_searchListingType !== nextParams?.pub_searchListingType ||
    pageOf(prevParams) !== pageOf(nextParams)
  );
};

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_LISTINGS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        searchInProgress: true,
        // Only a category-tab change drops the previous results, so the old type's cards
        // don't linger while the new query is in flight. Anything else (the client re-run
        // of loadData after SSR, a map move, a filter) keeps them visible until it lands.
        ...(replacesVisibleResults(state.searchParams, payload.searchParams)
          ? { currentPageResultIds: [], pagination: null }
          : {}),
        searchMapListingIds: [],
        searchListingsError: null,
      };
    case SEARCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        searchInProgress: false,
      };
    case SEARCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, searchInProgress: false, searchListingsError: payload };

    case SEARCH_MAP_SET_ACTIVE_LISTING:
      return {
        ...state,
        activeListingId: payload,
      };
    case FETCH_REVIEWS_REQUEST:
      return { ...state, reviewsInRequest: true, fetchReviewsError: null };
    case FETCH_REVIEWS_SUCCESS:
      return { ...state, reviewsInRequest: false, reviews: payload };
    case FETCH_REVIEWS_ERROR:
      return { ...state, reviewsInRequest: false, fetchReviewsError: payload };
    case SET_OCCUPANCY_PERCENTAGE:
      return { ...state, occupancyPercentage: payload };
    case SET_SEO_CANONICAL_PATH:
      return { ...state, seoCanonicalPath: payload };
    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const searchListingsRequest = searchParams => ({
  type: SEARCH_LISTINGS_REQUEST,
  payload: { searchParams },
});

export const searchListingsSuccess = response => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const searchListingsError = e => ({
  type: SEARCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const fetchReviewsRequest = () => ({ type: FETCH_REVIEWS_REQUEST });
export const fetchReviewsSuccess = response => ({ type: FETCH_REVIEWS_SUCCESS, payload: response });
export const fetchReviewsError = error => ({
  type: FETCH_REVIEWS_ERROR,
  error: true,
  payload: error,
});

// Vive en util/imageVariants.js: lo usan la búsqueda y la ficha. Se re-exporta aquí porque
// este duck es su único consumidor público desde que se escribió.
export { withoutSquareVariantsOnListingImages };

export const searchListings = (searchParams, config) => async (dispatch, getState, sdk) => {
  dispatch(searchListingsRequest(searchParams));

  // SearchPage can enforce listing query to only those listings with valid listingType
  // NOTE: this only works if you have set 'enum' type search schema to listing's public data fields
  //       - listingType
  //       Same setup could be expanded to 2 other extended data fields:
  //       - transactionProcessAlias
  //       - unitType
  //       ...and then turned enforceValidListingType config to true in configListing.js
  // Read More:
  // https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
  const searchValidListingTypes = listingTypes => {
    return config.listing.enforceValidListingType
      ? {
          pub_listingType: listingTypes.map(l => l.listingType),
          // pub_transactionProcessAlias: listingTypes.map(l => l.transactionType.alias),
          // pub_unitType: listingTypes.map(l => l.transactionType.unitType),
        }
      : {};
  };

  const datesSearchParams = (datesParam, timesParam) => {
    // const searchTZ = 'Etc/UTC';
    const searchTZ = 'Europe/Madrid';
    const datesFilter = config.search.defaultFilters.find(f => f.key === 'dates');
    const values = datesParam ? datesParam.split(',') : [];
    const timeValues = timesParam ? timesParam.split(',') : [];
    const hasExplicitTime = Array.isArray(timeValues) && timeValues.length === 2;
    const startTime = hasExplicitTime ? timeValues[0] : '00:00';
    const endTime = hasExplicitTime ? timeValues[1] : '18:00';
    const hasValues = datesFilter && datesParam && values.length === 2;
    const { dateRangeMode } = datesFilter || {};
    const isNightlyMode = dateRangeMode === 'night';

    // SearchPage need to use a single time zone but listings can have different time zones
    // We need to expand/prolong the time window (start & end) to cover other time zones too.

    // NOTE: you might want to consider changing UI so that
    //   1) location is always asked first before date range
    //   2) use some 3rd party service to convert location to time zone (IANA tz name)
    //   3) Make exact dates filtering against that specific time zone
    //   This setup would be better for dates filter,
    //   but it enforces a UX where location is always asked first and therefore configurability
    // const getProlongedStart = date => subtractTime(date, 14, 'hours', searchTZ);
    const getProlongedStart = date => subtractTime(date, 0, 'hours', searchTZ);
    const getProlongedEnd = date => addTimewith30mins(date, 23, 'hours', searchTZ);
    // const getProlongedEnd = date => addTime(date, 0, 'hours', searchTZ);

    const startDate = hasValues ? parseDateFromISO8601(values[0], startTime, searchTZ) : null;
    const endRaw = hasValues ? parseDateFromISO8601(values[1], endTime, searchTZ) : null;

    const endDate =
      hasValues && isNightlyMode
        ? endRaw
        : hasValues
        ? getExclusiveEndDate(endRaw, searchTZ)
        : null;

    const today = getStartOf(new Date(), 'day', searchTZ);
    const possibleStartDate = subtractTime(today, 14, 'hours', searchTZ);
    // const possibleStartDate = subtractTime(today, 0, 'hours', searchTZ);
    const hasValidDates =
      hasValues &&
      startDate.getTime() >= possibleStartDate.getTime() &&
      startDate.getTime() <= endDate.getTime();

    // minDuration = exact search window in minutes. time-partial with minDuration equal to the
    // full search duration means: only show listings that have the complete window free.
    // Any booking that overlaps the search range (even partially) reduces available time below
    // minDuration, so the listing is hidden. time-full alone does not catch partial overlaps
    // where the search window extends beyond the booked period.
    const searchDurationMinutes =
      hasValues && startDate && endRaw
        ? Math.round((endRaw.getTime() - startDate.getTime()) / (1000 * 60))
        : 0;

    // With an explicit time range the user asked for that exact slot, so require the whole
    // window free. With only a date (no time) the window is the default full day, and requiring
    // all of it free would hide almost every pool — so just require a realistic booking block free.
    const minDuration = hasExplicitTime ? searchDurationMinutes : DATE_ONLY_MIN_DURATION_MINUTES;

    return hasValidDates
      ? {
          start: startDate,
          end: endRaw,
          availability: 'time-partial',
          minDuration,
        }
      : {};
  };

  const stockFilters = datesMaybe => {
    const hasDatesFilterInUse = Object.keys(datesMaybe).length > 0;

    // If dates filter is not in use,
    //   1) Add minStock filter with default value (1)
    //   2) Add relaxed stockMode: "match-undefined"
    // The latter is used to filter out all the listings that explicitly are out of stock,
    // but keeps bookable and inquiry listings.
    return hasDatesFilterInUse ? {} : { minStock: 1, stockMode: 'match-undefined' };
  };

  const {
    perPage,
    price,
    dates,
    time,
    sort,
    origin,
    page: pageParam,
    discount,
    lluvia,
    interior,
    ...rest
  } = searchParams;
  // Los chips de lluvia e interior tampoco llegan a Flex tal cual: se traducen en `fetchSearch`.
  const shelterChips = {
    rain: lluvia === true || lluvia === 'true',
    indoor: interior === true || interior === 'true',
  };
  const isShelterFilterActive = shelterChips.rain || shelterChips.indoor;
  // `discount` never reaches Flex: it's resolved client-side (filterResponseByDiscount).
  // The value arrives as a boolean from `parse` or as 'true' once validated.
  const isDiscountFilterActive = discount === true || discount === 'true';
  const datesMaybe = datesSearchParams(dates, time);
  const stockMaybe = stockFilters(datesMaybe);
  // `relevance` (keyword) and `meta_rating` (host rating) are never sent to Flex — the former is a
  // keyword-search concept, the latter is sorted client-side from the author profile.
  const isClientSideSort =
    sort === config.search.sortConfig.relevanceKey || sort === RATINGS_SORT_KEY;
  const sortMaybe = isClientSideSort ? {} : { sort };

  const baseParams = {
    ...rest,
    ...datesMaybe,
    ...stockMaybe,
    ...searchValidListingTypes(config.listing.listingTypes),
  };

  const hasDatesFilterInUse = Object.keys(datesMaybe).length > 0;

  // Fetch the whole result set (and reorder for superhost priority) only on the
  // client: doing it during SSR would multiply both Render egress and SSR latency.
  const isBrowser = typeof window !== 'undefined';
  const fullFetchForSuperhosts = isBrowser && !hasDatesFilterInUse;

  // Apply the right pagination/perPage for the active fetch strategy.
  const withPaging = queryParams => {
    if (hasDatesFilterInUse) {
      // Flex rejects `page` when the availability filter is in use; pagination is handled
      // client-side by slicing the merged result in SearchResultsPanel.
      return { ...queryParams, perPage: DATES_PER_PAGE };
    }
    if (fullFetchForSuperhosts) {
      // Grab the first full page; the remaining pages are fetched in fetchFullResultSet.
      return { ...queryParams, perPage: SUPERHOST_FETCH_PER_PAGE, page: 1 };
    }
    return { ...queryParams, perPage, page: pageParam };
  };

  // Run one query and, on the client, expand it into the full result set: split dense
  // bounded date searches into 4 quadrants, or page through non-date searches. The merged
  // set is then reordered for superhosts and paginated client-side. Skipped during SSR
  // (a single page) to keep Render egress and SSR latency down; client hydration fills in.
  const fetchFromSharetribe = async p => {
    const firstResponse = await sdk.listings.query(p);
    const firstCount = firstResponse?.data?.data?.length || 0;

    if (isBrowser && hasDatesFilterInUse && firstCount === DATES_PER_PAGE && p.bounds) {
      const quadrants = splitBoundsIntoQuadrants(p.bounds);
      if (quadrants.length === 4) {
        const subResponses = await Promise.all(
          quadrants.map(quadrantBounds =>
            sdk.listings.query({ ...p, bounds: quadrantBounds }).catch(err => {
              console.warn('[search] quadrant query failed', err);
              return null;
            })
          )
        );
        return mergeSdkListingResponses([firstResponse, ...subResponses]) || firstResponse;
      }
    } else if (fullFetchForSuperhosts) {
      // Fetch every page so superhosts can be promoted regardless of where Flex paginated them.
      const meta = firstResponse?.data?.meta || {};
      const totalServerPages = Math.min(meta.totalPages || 1, SUPERHOST_MAX_FETCH_PAGES);
      const restResponses =
        totalServerPages > 1
          ? await Promise.all(
              Array.from({ length: totalServerPages - 1 }, (_, i) =>
                sdk.listings.query({ ...p, page: i + 2 }).catch(err => {
                  console.warn('[search] superhost page fetch failed', err);
                  return null;
                })
              )
            )
          : [];
      return mergeSdkListingResponses([firstResponse, ...restResponses]) || firstResponse;
    }

    return firstResponse;
  };

  // Searches can come from our own mirror of Sharetribe instead (0 credits; see
  // docs/mirror-search.md). The mirror answers the same params with the same response, so
  // everything after this point is unchanged. In the browser the whole result set comes in one
  // call, with the same cap as the page sweep above; with dates that also replaces the quadrant
  // split, and lifts Sharetribe's 100 per query. Any failure → Sharetribe, as before.
  const mirrorMode = hasDatesFilterInUse ? mirrorDatesMode() : mirrorSearchMode();
  const mirrorParamsFor = p =>
    fullFetchForSuperhosts || (isBrowser && hasDatesFilterInUse)
      ? { ...p, page: 1, perPage: SUPERHOST_FETCH_PER_PAGE * SUPERHOST_MAX_FETCH_PAGES }
      : p;
  // Only the browser's fetch is compared: it is the complete result the page is built on.
  const comparable = isBrowser && (fullFetchForSuperhosts || hasDatesFilterInUse);
  // With dates Sharetribe returns at most 100 per query: when it hits that, the mirror bringing
  // more is expected, not a difference (`tope=1` in the log).
  const shadowCapped = fromSharetribe =>
    hasDatesFilterInUse && (fromSharetribe?.data?.data?.length || 0) >= DATES_PER_PAGE;
  const shadowParamNames = queryParams => Object.keys(queryParams).sort();

  const fetchFullResultSet = async queryParams => {
    const p = { ...withPaging(queryParams), ...buildSearchListingsSdkFields(config) };

    if (mirrorMode === MIRROR_ON) {
      try {
        const fromMirror = await searchMirrorListings(mirrorParamsFor(p), config?.ssrApiBaseUrl);
        // Keep watching once switched on: a small sample also asks Sharetribe, in the background.
        if (comparable && Math.random() < MIRROR_WATCH_RATE) {
          fetchFromSharetribe(p)
            .then(fromSharetribe =>
              reportMirrorShadow({
                ...compareSearchResults(fromSharetribe, fromMirror),
                primary: 'mirror',
                capped: shadowCapped(fromSharetribe),
                params: shadowParamNames(queryParams),
              })
            )
            .catch(() => {});
        }
        return fromMirror;
      } catch (e) {
        console.warn('[search] mirror unavailable, using Sharetribe:', e.reason || e.message);
      }
    }

    const fromSharetribe = await fetchFromSharetribe(p);
    if (mirrorMode === MIRROR_SHADOW && comparable) {
      // Users still get Sharetribe's result; the mirror only gets compared against it.
      searchMirrorListings(mirrorParamsFor(p))
        .then(fromMirror =>
          reportMirrorShadow({
            ...compareSearchResults(fromSharetribe, fromMirror),
            primary: 'sharetribe',
            capped: shadowCapped(fromSharetribe),
            params: shadowParamNames(queryParams),
          })
        )
        .catch(() => {});
    }
    return fromSharetribe;
  };

  // Pestaña de jardines: además de los anuncios que ya son de jardín, entran las
  // piscinas cuyo anfitrión ofrece también el jardín (`pub_hasGarden`). Son dos
  // queries y no un filtro porque Flex no sabe expresar "de este tipo O con este otro
  // campo" sobre dos campos distintos; se fusionan por id con la misma utilidad que
  // usan los cuadrantes. Requiere índice de búsqueda para `hasGarden` (ver
  // docs/listing-faces.md); sin él la segunda query no filtra nada.
  const isGardenTab = rest.pub_searchListingType === GARDEN_FACE;
  const fetchAcrossFaces = async queryParams => {
    if (!isGardenTab) {
      return fetchFullResultSet(queryParams);
    }
    const responses = await Promise.all([
      fetchFullResultSet(queryParams),
      fetchFullResultSet({
        ...queryParams,
        pub_searchListingType: 'pool',
        pub_hasGarden: true,
      }).catch(err => {
        console.warn('[search] garden-face query failed', err);
        return null;
      }),
    ]);
    return mergeSdkListingResponses(responses) || responses[0];
  };

  // Si una query falla se descarta y, si no queda ninguna, el resultado es vacío: nunca el
  // universo sin filtrar. (Sin índice Flex no falla, ignora el campo; eso lo cubre
  // filterResponseByShelterChips más abajo.)
  const fetchSearch = async queryParams => {
    if (!isShelterFilterActive || isInterioresSearchTab(queryParams.pub_searchListingType)) {
      return fetchAcrossFaces(queryParams);
    }
    const responses = await Promise.all(
      shelterSearchQueries(queryParams, shelterChips).map(q =>
        fetchAcrossFaces(q).catch(err => {
          console.warn('[search] shelter chip query failed', err);
          return null;
        })
      )
    );
    return mergeSdkListingResponses(responses) || emptyListingsResponse();
  };

  // Relevance (pub_score) ordering needs two queries because Flex can't combine `sort` with
  // `origin`: one sorted by score (no origin) and one ordered by distance (no sort). Scored
  // listings come first (ranked by score), the rest by proximity — mirroring what the former
  // /searchListings proxy did server-side, but over the full result set so client-side
  // pagination is correct, and via the public SDK so only published listings are ever returned.
  const usesRelevanceMerge =
    !!sort && sort.includes('pub_score') && !!origin && isOriginInUse(config);

  const listingFields = config?.listing?.listingFields;
  const sanitizeConfig = { listingFields };

  try {
    let response;

    if (usesRelevanceMerge) {
      const [scoredResp, distanceResp] = await Promise.all([
        fetchSearch({ ...baseParams, ...sortMaybe }),
        fetchSearch({ ...baseParams, origin }),
      ]);
      response = mergeRelevanceResults(scoredResp, distanceResp);
    } else {
      const queryParams = { ...baseParams, ...sortMaybe };
      // Flex's default ordering is by distance from origin. Add it whenever Flex isn't doing the
      // sort (default, keyword relevance, or the client-side ratings sort) — it rejects
      // `sort` + `origin` together, so never add it alongside a Flex sort.
      if (!sortMaybe.sort) {
        queryParams.origin = origin;
      }
      response = await fetchSearch(queryParams);
    }

    if (hasDatesFilterInUse) {
      response = filterResponseByAdvanceNotice(response, dates, time);
      response = filterResponseByMinBookingDuration(response, dates, time);
    }

    if (isDiscountFilterActive) {
      response = filterResponseByDiscount(response);
    }

    if (isShelterFilterActive) {
      response = filterResponseByShelterChips(response, shelterChips);
    }

    // Enforce the category tab even when Flex ignores `pub_searchListingType` (see
    // filterResponseBySearchListingType). Todos leaves this null and keeps every type.
    response = filterResponseBySearchListingType(response, rest.pub_searchListingType);

    const dateYmd = dateYmdFromDatesParam(dates);
    response = filterResponseByDateFace(response, rest.pub_searchListingType, dateYmd);
    response = filterResponseByFaceCapacity(
      response,
      rest.pub_capacity,
      rest.pub_searchListingType,
      dateYmd
    );

    // "Precio total": solo con día, horas y grupo (sin ellos no hay total, y un `price` que
    // llegue en la URL se ignora). El máximo del slider se calcula antes de filtrar por precio,
    // para que no encoja al usarlo.
    const hours = searchDurationHours(dates, time);
    if (hours > 0 && rest.pub_capacity) {
      const totalsById = listingTotalsById(response, {
        searchListingType: rest.pub_searchListingType,
        dateYmd,
        capacity: rest.pub_capacity,
        hours,
        isWeekday: isWeekdayYmd(dateYmd),
      });
      response = withTotalPriceRange(response, totalsById, config.currency);
      if (price) {
        response = filterResponseByTotalPrice(
          response,
          price,
          totalsById,
          config.currency,
          response?.data?.meta?.totalPriceMax
        );
      }
    }
    // Only Todos mixes faces and types; a typed tab keeps the distance/sort order (its
    // listings already share the tab's face, see filterResponseByDateFace).
    const prioritizeFaces = r =>
      rest.pub_searchListingType == null ? applyTodosTypePriority(r, dateYmd) : r;

    if (isBrowser && (fullFetchForSuperhosts || hasDatesFilterInUse)) {
      response = applySuperhostSearchOrdering(response, {
        sort,
        origin,
        pageParam,
        capacity: rest.pub_capacity,
        prioritize: prioritizeFaces,
      });
    } else {
      response = prioritizeFaces(response);
    }

    response = withoutSquareVariantsOnListingImages(response);

    dispatch(addMarketplaceEntities(response, sanitizeConfig));
    dispatch(searchListingsSuccess(response));
    return response;
  } catch (e) {
    dispatch(searchListingsError(storableError(e)));
    throw e;
  }
};

export const fetchReviews = listingId => (dispatch, getState, sdk) => {
  return sdk.reviews.query({ listingId }).then(res => {
    const reviews = res?.data?.data;
    const reviewsLength = Array.isArray(reviews) && reviews.length;
    return reviewsLength;
  });
};

export const setActiveListing = listingId => ({
  type: SEARCH_MAP_SET_ACTIVE_LISTING,
  payload: listingId,
});

export const setOccupancyPercentage = percentage => ({
  type: SET_OCCUPANCY_PERCENTAGE,
  payload: percentage,
});

// Fetch the province occupancy for the searched date and store it for the banner.
// Best-effort: clears the banner when province/date are missing, and treats any
// failure as "no banner" (never throws into the search flow).
export const fetchProvinceOccupancy = ({ province, date }) => dispatch => {
  if (!province || !date) {
    dispatch(setOccupancyPercentage(null));
    return Promise.resolve(null);
  }
  return getProvinceOccupancy({ province, date })
    .then(res => {
      const pct = res && typeof res.percentage === 'number' ? res.percentage : null;
      dispatch(setOccupancyPercentage(pct));
      return pct;
    })
    .catch(() => {
      dispatch(setOccupancyPercentage(null));
      return null;
    });
};

/**
 * Canonical de la búsqueda: la landing SEO del municipio buscado que trata de lo mismo
 * (espacio y, si la hay, categoría del filtro). Va en el HTML del servidor, así que se espera en
 * el `loadData`; si algo falla, no hay canonical propio y la búsqueda se queda con el suyo.
 */
const fetchSeoCanonicalPath = ({ tab, queryParams }, config) => dispatch => {
  const citySlug = seoCityOfAddress(queryParams.address);
  if (!citySlug) {
    dispatch({ type: SET_SEO_CANONICAL_PATH, payload: null });
    return Promise.resolve();
  }
  return getSeoLandingPages(citySlug, config?.ssrApiBaseUrl).then(pages => {
    const path = seoLandingCanonicalPath({ tab, queryParams }, pages);
    dispatch({ type: SET_SEO_CANONICAL_PATH, payload: path });
  });
};

export const loadData = (params, search, config) => (dispatch, getState) => {
  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  const { tab = 'pool' } = typeof params === 'object' ? params : {};
  const searchListingType = pubSearchListingTypeForTab(tab);
  const { page = 1, address, origin, sort, ...rest } = queryParams;

  // Save search to user profile via backend — only when core params change, not on filter updates.
  // Anonymous searches wait in localStorage until signup/login (see util/pendingSearches.js).
  // Authenticated but currentUser not loaded yet (first client render, fetchCurrentUser still
  // in flight) is neither: it's dropped rather than kept as if it were anonymous.
  if (typeof window !== 'undefined') {
    const state = getState();
    const isAuthenticated = state?.auth?.isAuthenticated;
    const currentUser = state?.user?.currentUser;
    const canSave = isAuthenticated ? !!currentUser?.id : true;
    if (canSave) {
      const { dates, time, pub_capacity } = rest;
      const searchKey = [address, dates, time, pub_capacity, searchListingType, page].join('|');
      const coreParamsChanged = searchKey !== lastRecordedSearchKey;
      if (coreParamsChanged) {
        lastRecordedSearchKey = searchKey;
        const dateValues = typeof dates === 'string' ? dates.split(',') : [];
        const timeValues = typeof time === 'string' ? time.split(',') : [];
        const newSearch = {
          type: searchListingType,
          page,
          searchedAt: new Date().toISOString(),
        };
        if (address) newSearch.address = address;
        if (dateValues[0]) newSearch.date = dateValues[0];
        if (timeValues.length === 2) {
          newSearch.startTime = timeValues[0];
          newSearch.endTime = timeValues[1];
        }
        if (pub_capacity) newSearch.capacity = pub_capacity;
        if (isAuthenticated) {
          saveSearchHistory({ newSearch }).catch(() => {});
        } else {
          addPendingSearch(newSearch);
        }
      }
    }
  }

  // Province "high demand" banner: look up occupancy for the searched province +
  // date. Client-only (the endpoint is same-origin) and fire-and-forget so it never
  // blocks or breaks the search. No date or no derivable province → banner cleared.
  if (typeof window !== 'undefined') {
    const province = extractProvinceFromAddress(address);
    const dateValues = typeof rest.dates === 'string' ? rest.dates.split(',') : [];
    const date = dateValues[0] || null;
    dispatch(fetchProvinceOccupancy({ province, date }));
  }

  const originMaybe = isOriginInUse(config) && origin ? { origin } : {};

  const canonicalRequest = dispatch(
    fetchSeoCanonicalPath({ tab, queryParams: parse(search) }, config)
  );

  // Single search path: the duck's searchListings thunk runs everything through the public
  // marketplace SDK (published-only) and handles relevance (pub_score) ordering itself.
  const searchRequest = dispatch(
    searchListings(
      {
        ...(searchListingType ? { pub_searchListingType: searchListingType } : {}),
        ...rest,
        ...originMaybe,
        sort,
        page,
        perPage: RESULT_PAGE_SIZE,
        // searchListings() applies buildSearchListingsSdkFields(config) itself, so the
        // requested fields/include/image-variants are added there — don't duplicate them here.
      },
      config
    )
  );
  return Promise.all([searchRequest, canonicalRequest]).then(([searchResult]) => searchResult);
};
